import React, { useState, useEffect } from 'react'

import {
    Popover,
    Paper,
    Typography,
    Box,
    Button,
    TextField,
} from '@material-ui/core'

import { BaseWorkorder } from '../../../../models'

interface CostEditPopoverProps {
    open: boolean
    anchorEl: HTMLElement | null
    workorder: BaseWorkorder | null
    onClose: () => void
    onCostChange: (
        workorder: BaseWorkorder,
        addPrice: number | undefined,
        customPrice: number | undefined,
    ) => void
}

export const CostEditPopover = ({
    open,
    anchorEl,
    workorder,
    onClose,
    onCostChange,
}: CostEditPopoverProps) => {
    const [addPrice, setAddPrice] = useState<number | undefined>(0)
    const [customPrice, setCustomPrice] = useState<number | undefined>(
        undefined,
    )

    // Initialize form values when workorder changes
    useEffect(() => {
        if (workorder) {
            setAddPrice(workorder.add_price)
            setCustomPrice(workorder.custom_price || undefined)
        }
    }, [workorder])

    const handleSave = () => {
        if (workorder) {
            onCostChange(workorder, addPrice, customPrice)
            onClose()
        }
    }

    const handleCancel = () => {
        // Reset to original values
        if (workorder) {
            setAddPrice(workorder.add_price)
            setCustomPrice(workorder.custom_price || undefined)
        }
        onClose()
    }

    if (!workorder) return null

    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
            }}
            PaperProps={{
                style: {
                    padding: '20px',
                    minWidth: '300px',
                },
            }}
        >
            <Paper elevation={0}>
                <Box mb={3}>
                    <Typography variant="h6" gutterBottom>
                        Edit Workorder Cost
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        {workorder.service_name} - {workorder.unit_name}
                    </Typography>
                </Box>

                <Box mb={2}>
                    <TextField
                        fullWidth
                        label="Add Price ($)"
                        type="number"
                        variant="outlined"
                        value={addPrice || ''}
                        onChange={(e) => {
                            const value = e.target.value
                            if (value === '') {
                                setAddPrice(undefined)
                            } else {
                                setAddPrice(Number(value))
                            }
                        }}
                        InputProps={{
                            startAdornment: '$',
                        }}
                        size="small"
                    />
                </Box>

                <Box mb={3}>
                    <TextField
                        fullWidth
                        label="Custom Price ($)"
                        type="number"
                        variant="outlined"
                        value={customPrice || ''}
                        onChange={(e) => {
                            const value = e.target.value
                            if (value === '') {
                                setCustomPrice(undefined)
                            } else {
                                setCustomPrice(Number(value))
                            }
                        }}
                        InputProps={{
                            startAdornment: '$',
                        }}
                        size="small"
                        helperText="Leave empty to use default pricing"
                    />
                </Box>

                <Box display="flex" justifyContent="flex-end">
                    <Button
                        variant="outlined"
                        onClick={handleCancel}
                        style={{ marginRight: '8px' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSave}
                    >
                        Save
                    </Button>
                </Box>
            </Paper>
        </Popover>
    )
}
